import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, MessageCircle, Megaphone, User, LogOut, Send, Mail, Sparkles, Calendar, Loader2, ArrowRight, Sprout, Download, Flower2, PackagePlus, Tag, Truck, BookOpen, Gift, Users, Crown, Settings, Package } from "lucide-react";
import PortalHero from "../components/portal/PortalHero";
import FlowerStickers from "../components/FlowerStickers";
import { HERITAGE_ASSETS } from "../components/dahlias/heritageAssets";
import VipBoxBuilder from "./VipBoxBuilder";
import VipMysteryBox from "./VipMysteryBox";
import { VIP_BOX_FEATURE_ENABLED } from "../config/features";

/**
 * Open a printable HTML invoice / receipt in a new browser tab.
 * Adds the customer's auth token via a one-shot fetch so the backend can
 * authenticate the request, then opens the HTML in a fresh window for print-to-PDF.
 */
async function openInvoice(orderId) {
  try {
    const r = await customerApi.get(`/customer/orders/${orderId}/invoice`);
    const w = window.open("", "_blank", "noopener");
    if (!w) {
      window.alert("Pop-ups blocked, allow pop-ups for this site to download invoices.");
      return;
    }
    // Render invoice via a Blob URL instead of document.write, removes XSS surface
    // (we control the source, but document.write is a code-smell flagged by linters).
    const blob = new Blob([r.data], { type: "text/html" });
    const blobUrl = URL.createObjectURL(blob);
    w.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    // toast import only resolves at runtime, so use console + alert as fallback
    if (typeof window !== "undefined") window.alert("Couldn't open invoice, please try again.");
  }
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "heritage-customer-token";
const NAME_KEY = "heritage-customer-name";
const EMAIL_KEY = "heritage-customer-email";

const customerApi = axios.create({ baseURL: API, withCredentials: true });
customerApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function saveSession({ token, email, name }) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (email) localStorage.setItem(EMAIL_KEY, email);
  if (name) localStorage.setItem(NAME_KEY, name);
  window.dispatchEvent(new Event("heritage-customer-session"));
}

function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(NAME_KEY);
  window.dispatchEvent(new Event("heritage-customer-session"));
}

/* =================== Shell =================== */

function PortalShell({ children, tab, setTab, name, onLogout, vipEnabled, mysteryEnabled }) {
  const VIP_VISIBLE = VIP_BOX_FEATURE_ENABLED && vipEnabled;
  const MYSTERY_VISIBLE = !!mysteryEnabled;
  const TABS = [
    { v: "dashboard", label: "Dashboard", Icon: Sparkles },
    ...(VIP_VISIBLE ? [{ v: "vip-box", label: "Build A Box", Icon: PackagePlus }] : []),
    ...(MYSTERY_VISIBLE ? [{ v: "mystery-box", label: "Mystery Box", Icon: Sparkles }] : []),
    { v: "orders", label: "My orders", Icon: ShoppingBag },
    { v: "messages", label: "Heritage chat", Icon: MessageCircle },
    { v: "bulletin", label: "Bulletin", Icon: Megaphone },
    { v: "bloom-box", label: "Bloom Box", Icon: Mail },
    { v: "account", label: "My account", Icon: User },
  ];

  return (
    <div className="bloom-club-theme min-h-[100svh] bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)] font-sans relative">
      <FlowerStickers opacity={0.35} />
      <PortalHero
        compact
        kicker="✦ Heritage HQ ✦"
        title={
          <>
            Welcome{" "}
            <span className="font-display-italic text-[#F4C8D2]">
              back{name ? `, ${String(name).split(" ")[0]}` : ""}.
            </span>
          </>
        }
        userName={name}
        onLogout={onLogout}
      />

      {/* Elegant sticky tab rail */}
      <div
        data-testid="customer-tab-rail"
        className="sticky top-0 z-40 bg-[var(--dahlias-cream)]/95 backdrop-blur-md border-b border-[#6E1E3A]/15 shadow-sm"
      >
        <nav className="max-w-6xl mx-auto px-5 md:px-8 flex overflow-x-auto no-scrollbar gap-1">
          {TABS.map(({ v, label, Icon }) => {
            const active = tab === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                data-testid={`customer-tab-${v}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 md:px-4 py-3.5 text-[13px] md:text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-[#6E1E3A] text-[#6E1E3A] font-semibold"
                    : "border-transparent text-[var(--dahlias-ink)]/60 hover:text-[#6E1E3A]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </nav>
      </div>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">{children}</main>
    </div>
  );
}

/* =================== Orders tab =================== */

const STAGE_LABEL = {
  awaiting_payment:  "Awaiting payment",
  awaiting_inventory:"Awaiting inventory",
  assigned:          "Assigned",
  finalised:         "Ready to ship",
  shipping:          "Shipped",
  quality_control:   "Delivered",
  completed:         "Delivered",
  complaint:         "Under review",
};

function HqPaymentCountdown({ deadline, orderId }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => { clearInterval(id); };
  }, []);
  const due = new Date(deadline).getTime();
  if (Number.isNaN(due)) return null;
  const msLeft = due - now;
  if (msLeft <= 0) {
    return (
      <span data-testid={`hq-payment-expired-${orderId}`} className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#A33030]">
        Payment expired
      </span>
    );
  }
  const hh = Math.floor(msLeft / 3600000);
  const mm = Math.floor((msLeft % 3600000) / 60000);
  return (
    <span data-testid={`hq-payment-countdown-${orderId}`} className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#A33030]">
      {hh}h {mm.toString().padStart(2, "0")}m to pay
    </span>
  );
}

function OrdersTab() {
  const [data, setData] = useState({ online: [], manual: [], console: [], total_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await customerApi.get("/customer/orders");
        setData(r.data);
      } catch (e) {
        toast.error("Couldn't load orders.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadInvoice = async (orderId) => {
    // 1) Try the server-generated PDF (xhtml2pdf), newest, preferred path.
    try {
      const r = await customerApi.get(`/customer/orders/${orderId}/invoice/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      if (blob.size > 0) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `heritage-invoice-${orderId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
        toast.success("Invoice downloaded.");
        return;
      }
    } catch { /* generator unavailable, fall through */ }

    // 2) Try the admin-saved PDF (older flow)
    try {
      const r = await customerApi.get(`/customer/orders/${orderId}/invoice`);
      if (r.data?.pdf_base64) {
        // Decode base64 → Blob (data: URLs are blocked in modern browsers, Blob is the only reliable path)
        const byteChars = atob(r.data.pdf_base64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = r.data.filename || `heritage-invoice-${orderId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
        toast.success("Invoice downloaded.");
        return;
      }
    } catch { /* no admin PDF on file, fall through to HTML */ }

    // 2) Fallback , open the printable HTML in a new tab via a real anchor click.
    //    Anchor-triggered tabs ARE NOT popup-blocked the way window.open() is.
    try {
      const r2 = await customerApi.get(`/customer/orders/${orderId}/invoice/html`, { responseType: "text" });
      const blob = new Blob([r2.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success("Invoice opened, use Print → Save as PDF to download.");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Invoice not available yet.");
    }
  };

  if (loading) return <div className="text-[var(--dahlias-ink)]/60">Loading your orders…</div>;

  const all = [
    ...(data.online || []).map((o) => ({ ...o, _src: "online" })),
    ...(data.manual || []).map((o) => ({ ...o, _src: "manual" })),
    ...(data.console || []).map((o) => ({ ...o, _src: "console" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (all.length === 0) {
    return (
      <div
        data-testid="orders-empty"
        className="bg-[var(--dahlias-paper)] border-2 border-dashed border-[var(--dahlias-sage)]/30 p-10 text-center wobble-a"
      >
        <ShoppingBag className="w-10 h-10 text-[var(--dahlias-sage)] mx-auto mb-3" />
        <h3 className="font-display text-2xl text-[var(--dahlias-forest)]">No orders yet</h3>
        <p className="mt-2 text-sm text-[var(--dahlias-ink)]/65 max-w-md mx-auto">
          When you order tubers, blooms or join the Bloom Club, your orders will appear here with
          tracking, payment status, and the items you bought.
        </p>
        <Link
          to="/dahlias/store"
          className="inline-flex items-center gap-2 mt-5 px-5 h-11 wobble-pill bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-white text-sm"
        >
          Visit the store <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="font-display text-3xl text-[var(--dahlias-forest)]">
        Your orders <span className="text-[var(--dahlias-ink)]/50 text-xl">({all.length})</span>
      </h2>
      {all.map((o) => (
        <article
          key={o.id || o.order_number}
          data-testid={`customer-order-${o.id || o.order_number}`}
          className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-5"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs font-label tracking-[0.25em] uppercase text-[var(--dahlias-sage-deep)]">
                {new Date(o.created_at).toLocaleDateString("en-NZ", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}{" "}
                · {o._src === "online" ? "Online order" : o._src === "console" ? "Heritage HQ order" : "Heritage order"}
              </div>
              <div className="font-display text-xl text-[var(--dahlias-forest)] mt-0.5">
                {o.order_number || o.customer_name}
              </div>
              {o._src === "console" && o.tracking_number && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs">
                  <span className="font-label tracking-[0.25em] uppercase text-[var(--dahlias-sage-deep)]">Tracking</span>
                  <code className="bg-[var(--dahlias-cream)] px-1.5 py-0.5 text-[#6E1E3A]">{o.tracking_number}</code>
                  {o.shipping_provider && <span className="text-[var(--dahlias-ink)]/60">· {o.shipping_provider}</span>}
                </div>
              )}
              {/* Invoice button , always shown so customers can grab a receipt
                  for every order. Falls back to the printable HTML invoice if
                  there's no admin-rendered PDF on file. */}
              <button
                type="button"
                onClick={() => downloadInvoice(o.id)}
                data-testid={`hq-invoice-${o.id}`}
                className="mt-2 inline-flex items-center gap-1.5 px-3 h-8 wobble-pill bg-[#6E1E3A] hover:bg-[#5a1830] text-white text-[11px] font-label tracking-[0.25em] uppercase"
              >
                ↓ Invoice{o.invoice?.version ? ` (v${o.invoice.version})` : ""}
              </button>
            </div>
            <div className="flex flex-col items-end gap-1">
              {o._src === "console" && o.stage && (
                <span
                  className="px-2 py-0.5 wobble-pill text-[10px] uppercase tracking-[0.2em] bg-[#6E1E3A] text-white"
                  data-testid={`hq-order-stage-${o.id}`}
                >
                  {STAGE_LABEL[o.stage] || o.stage}
                </span>
              )}
              <span
                className={`px-2 py-0.5 wobble-pill text-[10px] uppercase tracking-[0.2em] ${
                  o.paid || o.payment_status === "paid"
                    ? "bg-[var(--dahlias-sage)] text-white"
                    : "bg-[var(--dahlias-clay)]/15 text-[var(--dahlias-clay)]"
                }`}
              >
                {o.paid || o.payment_status === "paid" ? "Paid" : "Awaiting"}
              </span>
              {Number(o.amount || o.total) > 0 && (
                <span className="font-display text-lg text-[var(--berry)]">
                  ${(Number(o.amount || o.total)).toFixed(2)}
                </span>
              )}
              {o._src === "console" && o.stage === "awaiting_payment" && o.payment_due_at && (
                <HqPaymentCountdown deadline={o.payment_due_at} orderId={o.id} />
              )}
            </div>
          </div>

          {/* Items */}
          {o.items && (
            <pre className="mt-3 whitespace-pre-wrap text-sm text-[var(--dahlias-ink)] font-sans">
              {o.items}
            </pre>
          )}
          {Array.isArray(o.line_items) && o.line_items.length > 0 && !o.items && (
            <ul className="mt-3 space-y-1 text-sm">
              {o.line_items.map((li, i) => (
                <li key={i}>
                  {li.qty}× {li.name}{li.variant_label ? `, ${li.variant_label}` : ""}
                  {li.unit_price > 0 ? ` (NZ$${Number(li.unit_price).toFixed(2)})` : ""}
                </li>
              ))}
            </ul>
          )}
          {Array.isArray(o.items_list) && (
            <ul className="mt-3 space-y-1 text-sm">
              {o.items_list.map((li, i) => (
                <li key={i}>{li.qty}× {li.name}</li>
              ))}
            </ul>
          )}

          {/* Tracking */}
          {o.tracking_number && (
            <div className="mt-3 text-xs text-[var(--dahlias-ink)]/70">
              Tracking: <span className="font-mono">{o.tracking_number}</span>
            </div>
          )}
          {o.status && (
            <div className="mt-1 text-xs text-[var(--dahlias-ink)]/65 capitalize">
              Status: {o.status}
            </div>
          )}

          {/* Invoice / receipt download */}
          {o.id && (
            <div className="mt-4 pt-3 border-t border-[var(--dahlias-sage)]/15 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => openInvoice(o.id)}
                data-testid={`customer-invoice-${o.id}`}
                className="inline-flex items-center gap-1.5 px-3 h-9 wobble-pill border border-[#6E1E3A]/40 text-[#6E1E3A] hover:bg-[#6E1E3A]/10 text-xs font-label tracking-[0.25em] uppercase"
              >
                <Download className="w-3.5 h-3.5" />
                {o.paid || o.payment_status === "paid" ? "Receipt" : "Invoice"}
              </button>
              {o._src === "console" && o.payment_status !== "paid" && (
                <HqPayNowButton order={o} />
              )}
            </div>
          )}

          {/* Build-a-Box / customer picks */}
          {o._src === "manual" && Number(o.pick_count) > 0 && (
            <OrderPicksBlock order={o} />
          )}
          {o._src === "console" && Number(o.hq_pick_count) > 0 && (
            <ConsolePicksBlock order={o} />
          )}
          {o._src === "console" && (
            <ConsoleOrderNote orderId={o.id} />
          )}
        </article>
      ))}
    </div>
  );
}

/* =================== Pay Now (Stripe) button for unpaid HQ orders ============ */
function HqPayNowButton({ order }) {
  const [busy, setBusy] = useState(false);
  const onPay = async () => {
    setBusy(true);
    try {
      const r = await customerApi.post(`/pay/order/${order.id}`, {
        email: order.email || "",
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error("Couldn't get pay link.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start payment.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onPay}
      disabled={busy}
      data-testid={`hq-pay-now-${order.id}`}
      className="inline-flex items-center gap-1.5 px-3 h-9 wobble-pill bg-[#7B4F82] hover:bg-[#5d3a64] text-white text-xs font-label tracking-[0.25em] uppercase disabled:opacity-60"
    >
      {busy ? "Opening Stripe…" : "Pay now (Stripe)"}
    </button>
  );
}

/* =================== Order picks (build-your-own box on a manual order) ===== */

function OrderPicksBlock({ order }) {
  const [catalog, setCatalog] = useState([]);
  const [picked, setPicked] = useState(order.picked_variety_ids || []);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/catalog`);
        if (mounted) setCatalog(Array.isArray(r.data) ? r.data : []);
      } catch (e) { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  const pool = useMemo(() => {
    const allowed = order.pick_pool_variety_ids;
    if (Array.isArray(allowed) && allowed.length > 0) {
      const set = new Set(allowed);
      return catalog.filter((v) => set.has(v.id));
    }
    return catalog;
  }, [catalog, order.pick_pool_variety_ids]);

  const limit = Number(order.pick_count) || 0;
  const remaining = limit - picked.length;
  const submitted = !!order.inventoried && Array.isArray(order.picked_variety_ids) && order.picked_variety_ids.length >= limit;

  const toggle = (id) => {
    if (picked.includes(id)) {
      setPicked(picked.filter((x) => x !== id));
    } else if (picked.length < limit) {
      setPicked([...picked, id]);
    } else {
      toast.error(`You can only pick ${limit} varieties.`);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await customerApi.post(`/customer/orders/${order.id}/picks`, { variety_ids: picked });
      toast.success("Picks saved, Heritage will be in touch.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save picks.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-[#6E1E3A]/20" data-testid={`order-picks-${order.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div>
          <div className="font-label tracking-[0.3em] text-[10px] uppercase text-[#6E1E3A]">✦ Build-a-Box ✦</div>
          <div className="mt-1 font-display text-lg text-[var(--dahlias-forest)] leading-tight">
            Choose your <span className="font-display-italic text-[#6E1E3A]">{limit}</span> varieties
            {Number(order.mystery_count) > 0 && (
              <span className="text-sm text-[var(--dahlias-ink)]/65"> · plus {order.mystery_count} mystery</span>
            )}
          </div>
          <div className="text-xs text-[var(--dahlias-ink)]/70 mt-0.5">
            {picked.length} / {limit} chosen · {remaining > 0 ? `${remaining} to go` : "Box is full"}
          </div>
        </div>
        <span className="text-sm text-[#6E1E3A]">{open ? "Hide" : "Open"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {pool.length === 0 ? (
            <p className="text-sm text-[var(--dahlias-ink)]/65 italic">
              Heritage is preparing the variety pool for this order. Check back soon.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {pool.map((v) => {
                const isPicked = picked.includes(v.id);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => toggle(v.id)}
                    disabled={submitted}
                    data-testid={`order-pick-${order.id}-${v.id}`}
                    className={`group relative aspect-square overflow-hidden rounded-full border-[3px] transition ${
                      isPicked
                        ? "border-[#6E1E3A] shadow-lg ring-2 ring-[#6E1E3A]/30"
                        : "border-[#6E1E3A]/30 hover:border-[#6E1E3A]/70"
                    } ${submitted ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {(v.tile_image || v.image) ? (
                      <img src={v.tile_image || v.image} alt={v.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <span className="absolute inset-0 grid place-items-center text-[#FBE6D8]" style={{ background: "radial-gradient(circle at 30% 25%, #F4C8D2 0%, #C9508A 55%, #6E1E3A 100%)" }}>
                        <Sparkles className="w-7 h-7" />
                      </span>
                    )}
                    <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] py-1.5 px-2 leading-tight text-center font-display">{v.name}</span>
                    {isPicked && (
                      <span className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-[#6E1E3A] text-[#FBE6D8] grid place-items-center shadow">
                        <Sparkles className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={save}
            disabled={saving || submitted}
            data-testid={`order-picks-save-${order.id}`}
            className="inline-flex items-center gap-1.5 h-10 px-5 wobble-pill bg-[#6E1E3A] hover:bg-[#8a2548] text-[#FBE6D8] text-xs font-label tracking-[0.3em] uppercase disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {submitted ? "Submitted" : "Save my picks"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ============= Console order , HQ catalogue picks (admin-set count) ===== */

function ConsolePicksBlock({ order }) {
  const [catalog, setCatalog] = useState([]);
  const [picked, setPicked] = useState(order.hq_picked_variety_ids || []);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/catalog`);
        if (mounted) setCatalog(Array.isArray(r.data) ? r.data : []);
      } catch (e) { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  const limit = Number(order.hq_pick_count) || 0;
  const remaining = limit - picked.length;

  const toggle = (id) => {
    if (picked.includes(id)) {
      setPicked(picked.filter((x) => x !== id));
    } else if (picked.length < limit) {
      setPicked([...picked, id]);
    } else {
      toast.error(`You can only pick ${limit} ${limit === 1 ? "variety" : "varieties"}.`);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await customerApi.post(`/customer/orders/${order.id}/console-picks`, { variety_ids: picked });
      toast.success("Picks saved, Heritage will allocate them to your order.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save picks.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-[#6E1E3A]/20" data-testid={`hq-picks-${order.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div>
          <div className="font-label tracking-[0.3em] text-[10px] uppercase text-[#6E1E3A]">✦ Pick from catalogue ✦</div>
          <div className="mt-1 font-display text-lg text-[var(--dahlias-forest)] leading-tight">
            Choose your <span className="font-display-italic text-[#6E1E3A]">{limit}</span> {limit === 1 ? "tuber" : "tubers"}
          </div>
          <div className="text-xs text-[var(--dahlias-ink)]/70 mt-0.5">
            {picked.length} / {limit} chosen · {remaining > 0 ? `${remaining} to go` : "Full"}
          </div>
        </div>
        <span className="text-sm text-[#6E1E3A]">{open ? "Hide" : "Open"}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {catalog.length === 0 ? (
            <p className="text-sm text-[var(--dahlias-ink)]/65 italic">Loading catalogue…</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {catalog.map((v) => {
                const isPicked = picked.includes(v.id);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => toggle(v.id)}
                    data-testid={`hq-pick-${order.id}-${v.id}`}
                    className={`group relative aspect-square overflow-hidden rounded-full border-[3px] transition ${
                      isPicked
                        ? "border-[#6E1E3A] shadow-lg ring-2 ring-[#6E1E3A]/30"
                        : "border-[#6E1E3A]/30 hover:border-[#6E1E3A]/70"
                    }`}
                  >
                    {(v.tile_image || v.image) ? (
                      <img src={v.tile_image || v.image} alt={v.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <span className="absolute inset-0 grid place-items-center text-[#FBE6D8]" style={{ background: "radial-gradient(circle at 30% 25%, #F4C8D2 0%, #C9508A 55%, #6E1E3A 100%)" }}>
                        <Sparkles className="w-7 h-7" />
                      </span>
                    )}
                    <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] py-1.5 px-2 leading-tight text-center font-display">{v.name}</span>
                    {isPicked && (
                      <span className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-[#6E1E3A] text-[#FBE6D8] grid place-items-center shadow">
                        <Sparkles className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid={`hq-picks-save-${order.id}`}
            className="inline-flex items-center gap-1.5 h-10 px-5 wobble-pill bg-[#6E1E3A] hover:bg-[#8a2548] text-[#FBE6D8] text-xs font-label tracking-[0.3em] uppercase disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Save my picks
          </button>
        </div>
      )}
    </div>
  );
}

/* ============= Console order , Add Note (creates an admin message) ===== */

function ConsoleOrderNote({ orderId }) {
  const [body, setBody] = useState("");
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await customerApi.post(`/customer/orders/${orderId}/note`, { body: body.trim(), order_id: orderId });
      toast.success("Note sent to Heritage.");
      setBody("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send note.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[var(--dahlias-sage)]/15">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`order-note-open-${orderId}`}
          className="text-[11px] tracking-[0.25em] uppercase text-[#6E1E3A] hover:underline inline-flex items-center gap-1"
        >
          + Add a note about this order
        </button>
      ) : (
        <div className="space-y-2">
          <textarea
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="e.g. Can I combine this with another order? Or, please add an extra variety."
            data-testid={`order-note-input-${orderId}`}
            className="w-full px-3 py-2 text-sm bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/30 rounded resize-none focus:outline-none focus:border-[#6E1E3A]"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={send}
              disabled={sending || !body.trim()}
              data-testid={`order-note-send-${orderId}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 wobble-pill bg-[#6E1E3A] hover:bg-[#8a2548] text-white text-[10px] tracking-[0.25em] uppercase disabled:opacity-40"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send note
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setBody(""); }}
              className="text-[10px] tracking-[0.25em] uppercase text-[var(--dahlias-ink)]/60 hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================== Messages tab =================== */

function MessagesTab({ name }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const r = await customerApi.get("/customer/messages");
      setMessages(r.data || []);
    } catch (e) {
      toast.error("Couldn't load messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await customerApi.get("/customer/messages");
        if (mounted) setMessages(r.data || []);
      } catch (e) {
        if (mounted) toast.error("Couldn't load messages.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await customerApi.post("/customer/messages", { body: body.trim() });
      setBody("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="font-display text-3xl text-[var(--dahlias-forest)]">Direct line to Heritage</h2>
        <p className="mt-1 text-sm text-[var(--dahlias-ink)]/70">
          Hi {name?.split(" ")[0] || "there"}, say kia ora, ask about an order, or get advice.
          We&apos;ll reply by email too.
        </p>
      </div>

      <div
        data-testid="messages-thread"
        className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/20 wobble-a p-5 min-h-[280px] max-h-[60vh] overflow-y-auto space-y-3"
      >
        {loading ? (
          <div className="text-[var(--dahlias-ink)]/60 text-sm">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-[var(--dahlias-ink)]/55 italic text-sm py-8">
            No messages yet. Be the first to say hi 🌸
          </div>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              data-testid={`message-${m.id}`}
              className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] px-4 py-2 wobble-pill text-sm leading-relaxed ${
                  m.sender === "customer"
                    ? "bg-[var(--berry)] text-white"
                    : "bg-[var(--dahlias-sage)]/15 border border-[var(--dahlias-sage)]/30 text-[var(--dahlias-ink)]"
                }`}
              >
                <div className="whitespace-pre-wrap">{m.body}</div>
                <div className={`text-[10px] mt-1 ${m.sender === "customer" ? "text-white/65" : "text-[var(--dahlias-ink)]/50"}`}>
                  {new Date(m.created_at).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })}
                  {m.sender === "admin" ? " · Heritage" : ""}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Type your message…"
          data-testid="message-input"
          className="flex-1 px-4 py-3 wobble-pill bg-white border border-[var(--dahlias-sage)]/30 text-sm resize-none focus:outline-none focus:border-[var(--dahlias-sage-deep)]"
        />
        <button
          onClick={send}
          disabled={sending || !body.trim()}
          data-testid="message-send"
          className="self-stretch px-5 wobble-pill bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white disabled:opacity-50 inline-flex items-center gap-2"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send
        </button>
      </div>
    </div>
  );
}

/* =================== Bulletin tab =================== */

function BulletinTab() {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await customerApi.get("/customer/bulletins");
        setBulletins(r.data || []);
      } catch (e) {
        // soft
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Auto-generated tiles always shown
  const AUTO_TILES = [
    {
      key: "catalogue",
      title: "Catalogue Release",
      subtitle: "Monday 8 June 2026, 5pm NZT",
      blurb: "Browse the full 2025-26 collection and register interest before the public release.",
      link: "/dahlias/catalog",
      cta: "Open the catalogue",
      icon: Calendar,
      tone: "forest",
    },
    {
      key: "store",
      title: "2026 Tubers · Mystery Boxes",
      subtitle: "All three release together",
      blurb: "Hand-divided through winter. Register interest from the store for the early-access note.",
      link: "/dahlias/store",
      cta: "Visit the store",
      icon: Sparkles,
      tone: "clay",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Auto-generated */}
      <section>
        <h2 className="font-display text-3xl text-[var(--dahlias-forest)]">Heritage Bulletin</h2>
        <p className="mt-1 text-sm text-[var(--dahlias-ink)]/70">
          What&apos;s happening at the farm, special access, and notes from us.
        </p>

        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUTO_TILES.map((t) => {
            const Icon = t.icon;
            const tones = {
              forest: { bg: "var(--dahlias-forest)", fg: "var(--dahlias-cream)" },
              clay: { bg: "var(--dahlias-clay)", fg: "var(--dahlias-cream)" },
              berry: { bg: "var(--berry)", fg: "white" },
            }[t.tone];
            return (
              <Link
                key={t.key}
                to={t.link}
                data-testid={`bulletin-auto-${t.key}`}
                className="block wobble-a p-5 transition hover:-translate-y-0.5 hover:shadow-lg"
                style={{ background: tones.bg, color: tones.fg }}
              >
                <Icon className="w-6 h-6 opacity-90" />
                <h3 className="mt-3 font-display text-2xl leading-tight">{t.title}</h3>
                <div className="text-xs font-label tracking-[0.25em] uppercase mt-1 opacity-80">
                  {t.subtitle}
                </div>
                <p className="mt-3 text-sm opacity-90 leading-relaxed">{t.blurb}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium underline-offset-2 hover:underline">
                  {t.cta} <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Admin posted */}
      <section>
        <h3 className="font-label tracking-[0.35em] text-[11px] text-[var(--dahlias-clay)] uppercase">
          ✦ Special opportunities ✦
        </h3>
        {loading ? (
          <div className="text-sm text-[var(--dahlias-ink)]/55 mt-3">Loading…</div>
        ) : bulletins.length === 0 ? (
          <div className="mt-3 text-sm text-[var(--dahlias-ink)]/55 italic">
            No special deals posted yet, check back soon.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {bulletins.map((b) => (
              <article
                key={b.id}
                data-testid={`bulletin-${b.id}`}
                className="bg-[var(--dahlias-paper)] border-l-4 border-[var(--berry)] wobble-a p-5"
              >
                {b.image_url && (
                  <img
                    src={b.image_url}
                    alt=""
                    className="w-full max-h-48 object-cover mb-3 rounded"
                  />
                )}
                <h4 className="font-display text-2xl text-[var(--dahlias-forest)]">{b.title}</h4>
                {b.body && (
                  <p className="mt-2 text-sm text-[var(--dahlias-ink)]/85 whitespace-pre-wrap leading-relaxed">
                    {b.body}
                  </p>
                )}
                {b.cta_url && (
                  <a
                    href={b.cta_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 px-4 h-10 wobble-pill bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white text-sm font-medium"
                  >
                    {b.cta_label || "Learn more"} <ArrowRight className="w-4 h-4" />
                  </a>
                )}
                <div className="mt-2 text-[10px] font-label tracking-[0.25em] uppercase text-[var(--dahlias-ink)]/45">
                  Posted {new Date(b.created_at).toLocaleDateString("en-NZ", { dateStyle: "medium" })}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* =================== Account tab =================== */

function AccountTab({ me, onSaved }) {
  const [name, setName] = useState(me?.name || "");
  const [phone, setPhone] = useState(me?.phone || "");
  const [address, setAddress] = useState(me?.address || "");
  const [saving, setSaving] = useState(false);
  const [profilePic, setProfilePic] = useState(me?.profile_picture || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      const r = await customerApi.patch("/customer/me", { name, phone, address });
      onSaved({ ...r.data, profile_picture: profilePic });
      toast.success("Profile saved.");
    } catch (e) {
      toast.error("Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const uploadPic = async (file) => {
    if (!file) return;
    if (file.size > 1_800_000) {
      toast.error("Image must be under 1.8 MB.");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const base64 = String(dataUrl).split(",", 2)[1];
      const r = await customerApi.post("/customer/profile-picture", { image_base64: base64, mime: file.type || "image/jpeg" });
      setProfilePic(r.data?.profile_picture || dataUrl);
      onSaved({ ...me, name, phone, address, profile_picture: r.data?.profile_picture || dataUrl });
      toast.success("Profile picture updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload.");
    } finally {
      setUploading(false);
    }
  };

  const removePic = async () => {
    if (!window.confirm("Remove your profile picture?")) return;
    setUploading(true);
    try {
      await customerApi.delete("/customer/profile-picture");
      setProfilePic(null);
      onSaved({ ...me, name, phone, address, profile_picture: null });
      toast.success("Removed.");
    } catch (e) {
      toast.error("Couldn't remove.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-xl space-y-5">
      <h2 className="font-display text-3xl text-[var(--dahlias-forest)]">Your account</h2>
      <p className="text-sm text-[var(--dahlias-ink)]/70">
        Email: <span className="font-mono">{me?.email}</span>
      </p>

      {/* Profile picture */}
      <div className="flex items-center gap-4 p-4 wobble-a bg-[var(--dahlias-paper)] border border-[var(--dahlias-sage)]/25" data-testid="account-profile-pic">
        <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#6E1E3A]/30 bg-[var(--dahlias-cream)] grid place-items-center text-[#6E1E3A]">
          {profilePic ? (
            <img src={profilePic} alt="Your profile" className="w-full h-full object-cover" data-testid="account-profile-img" />
          ) : (
            <span className="font-display text-3xl">{(name || me?.email || "?").charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="flex-1">
          <div className="text-xs font-label tracking-[0.25em] uppercase text-[var(--dahlias-sage-deep)]">
            Profile picture
          </div>
          <div className="text-[11px] text-[var(--dahlias-ink)]/60 mt-1">
            JPEG, PNG, or WEBP, under 1.8 MB.
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPic(f); e.target.value = ""; }}
              className="hidden"
              data-testid="account-profile-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="account-profile-upload"
              className="inline-flex items-center gap-1.5 h-9 px-3 wobble-pill bg-[#6E1E3A] hover:bg-[#8a2548] text-white text-[10px] tracking-[0.25em] uppercase disabled:opacity-60"
            >
              {uploading ? "Uploading…" : (profilePic ? "Change" : "Upload")}
            </button>
            {profilePic && (
              <button
                type="button"
                onClick={removePic}
                disabled={uploading}
                data-testid="account-profile-remove"
                className="inline-flex items-center h-9 px-3 wobble-pill border border-[#A33030]/40 text-[#A33030] hover:bg-[#A33030]/10 text-[10px] tracking-[0.25em] uppercase"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="text-xs font-label tracking-[0.25em] uppercase text-[var(--dahlias-sage-deep)]">
          Name
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="account-name"
          className="mt-1 w-full h-11 px-4 wobble-pill bg-white border border-[var(--dahlias-sage)]/30 text-sm"
        />
      </div>
      <div>
        <label className="text-xs font-label tracking-[0.25em] uppercase text-[var(--dahlias-sage-deep)]">
          Phone
        </label>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid="account-phone"
          className="mt-1 w-full h-11 px-4 wobble-pill bg-white border border-[var(--dahlias-sage)]/30 text-sm"
        />
      </div>
      <div>
        <label className="text-xs font-label tracking-[0.25em] uppercase text-[var(--dahlias-sage-deep)]">
          Default shipping address
        </label>
        <textarea
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          rows={3}
          data-testid="account-address"
          className="mt-1 w-full px-4 py-3 wobble-a bg-white border border-[var(--dahlias-sage)]/30 text-sm"
        />
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="account-save"
        className="inline-flex items-center gap-2 h-11 px-5 wobble-pill bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white text-sm font-medium disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

/* =================== Auth pages =================== */

export function CustomerSignup() {
  const nav = useNavigate();
  const loc = useLocation();
  const prefillEmail = useMemo(() => {
    const params = new URLSearchParams(loc.search);
    return params.get("email") || "";
  }, [loc.search]);
  const [form, setForm] = useState({ name: "", email: prefillEmail, phone: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/customer/signup`, form, { withCredentials: true });
      saveSession(r.data);
      nav(loc.state?.from || "/account");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed.");
    } finally {
      setBusy(false);
    }
  };

  return <AuthPage title="Create your Heritage account" subtitle="So your orders, messages and bulletins all live in one place." testid="customer-signup-page">
    <form onSubmit={submit} className="space-y-4">
      <AuthField label="Your name *">
        <input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="auth-input" />
      </AuthField>
      <AuthField label="Email *">
        <input data-testid="signup-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="auth-input" />
      </AuthField>
      <AuthField label="Phone (optional)">
        <input data-testid="signup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="auth-input" />
      </AuthField>
      <AuthField label="Password (8+ characters) *">
        <input data-testid="signup-password" required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="auth-input" />
      </AuthField>
      <button type="submit" disabled={busy} data-testid="signup-submit" className="auth-submit">
        {busy ? "Creating…" : "Create account"}
      </button>
      <p className="text-sm text-[var(--dahlias-ink)]/70 text-center">
        Already have an account?{" "}
        <Link to="/account/login" state={loc.state} data-testid="signup-to-login" className="text-[var(--dahlias-clay)] underline">Sign in</Link>
      </p>
    </form>
  </AuthPage>;
}

export function CustomerLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSent, setLinkSent] = useState("");

  // One-time login link , no password needed.
  const sendLoginLink = async () => {
    if (!form.email.trim()) { toast.error("Pop your email in first."); return; }
    setLinkBusy(true);
    setLinkSent("");
    try {
      const r = await axios.post(`${API}/customer/access/login-link`, { email: form.email.trim() });
      setLinkSent(r.data?.message || "Check your inbox for your login link.");
      if (r.data?.ok) toast.success("Login link on its way."); else toast.error(r.data?.message || "No account for that email yet.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send a login link just now.");
    } finally {
      setLinkBusy(false);
    }
  };

  // Request-access form state
  const [showReq, setShowReq] = useState(false);
  const [reqForm, setReqForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [reqBusy, setReqBusy] = useState(false);
  const [reqDone, setReqDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/customer/login`, form, { withCredentials: true });
      saveSession(r.data);
      nav(loc.state?.from || "/account");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  const submitReq = async (e) => {
    e.preventDefault();
    if (!reqForm.email.trim()) {
      toast.error("Please share your email.");
      return;
    }
    try {
      setReqBusy(true);
      await axios.post(`${API}/portal-access-request`, reqForm);
      setReqDone(true);
      toast.success("Request sent, we'll be in touch shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send request.");
    } finally {
      setReqBusy(false);
    }
  };

  return (
    <div
      data-testid="customer-login-page"
      className="min-h-[100svh] bg-[var(--dahlias-cream)] flex items-center justify-center p-5"
    >
      <div className="w-full max-w-4xl grid md:grid-cols-2 gap-6 md:gap-8">
        {/* Left, portal explanation */}
        <aside className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-7 md:p-9">
          <Link to="/" className="block">
            <img
              src="https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/wvt1q19k_file_000000004d8071fa9f0970c0480bd558.png"
              alt="Heritage Headquarters"
              className="w-24 h-24 object-contain"
            />
          </Link>
          <span className="mt-4 inline-block font-label tracking-[0.4em] text-[10px] uppercase text-[var(--berry)]">
            ✦ The Customer Portal ✦
          </span>
          <h1 className="mt-2 font-display text-3xl md:text-4xl text-[var(--dahlias-forest)] leading-tight">
            Welcome to <span className="font-display-italic text-[var(--berry)]">Heritage Headquarters.</span>
          </h1>
          <p className="mt-4 text-sm md:text-base text-[var(--dahlias-ink)]/80 leading-relaxed">
            Heritage Headquarters is your private corner of Heritage Dahlias, where you can:
          </p>
          <ul className="mt-4 space-y-2.5 text-sm md:text-[15px] text-[var(--dahlias-ink)]/85">
            <li className="flex gap-2"><span className="text-[var(--berry)]">✦</span> Track your tuber orders & download invoices</li>
            <li className="flex gap-2"><span className="text-[var(--berry)]">✦</span> Message us directly, no public inbox</li>
            <li className="flex gap-2"><span className="text-[var(--berry)]">✦</span> Pre-register interest in next season&apos;s Bloom Box</li>
            <li className="flex gap-2"><span className="text-[var(--berry)]">✦</span> Exclusive variety drops and early access</li>
          </ul>
          <div className="mt-6 p-4 rounded-lg bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/20">
            <p className="text-xs md:text-sm text-[var(--dahlias-ink)]/80 leading-relaxed">
              <b className="text-[var(--berry)]">No account yet?</b> Anyone can create a Heritage HQ account. Existing orders are matched automatically by exact email, and you can report a missing order from inside HQ.
            </p>
          </div>
        </aside>

        {/* Right, login OR request-access */}
        <section className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-7 md:p-9">
          {!showReq ? (
            <>
              <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">Sign in</h2>
              <p className="mt-1.5 text-sm text-[var(--dahlias-ink)]/70">
                Use the email you placed your order under.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <AuthField label="Email">
                  <input
                    data-testid="login-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="auth-input"
                  />
                </AuthField>
                <AuthField label="Password">
                  <input
                    data-testid="login-password"
                    required
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="auth-input"
                  />
                </AuthField>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <button
                    type="button"
                    data-testid="login-send-link"
                    disabled={linkBusy}
                    onClick={sendLoginLink}
                    className="text-sm text-[var(--dahlias-forest)] underline underline-offset-4 disabled:opacity-50"
                  >
                    {linkBusy ? "Sending…" : "Email me a login link"}
                  </button>
                  <Link to="/account/forgot-password" state={loc.state} data-testid="login-forgot-password" className="text-sm text-[var(--berry)] underline underline-offset-4">Forgot Password?</Link>
                </div>
                {linkSent && (
                  <p data-testid="login-link-sent" className="text-xs text-[var(--dahlias-ink)]/70 bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/25 rounded p-2.5">
                    {linkSent}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit"
                  className="auth-submit"
                >
                  {busy ? "Signing in…" : "Sign in to Heritage Headquarters"}
                </button>
              </form>
              <div className="mt-4 flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[var(--dahlias-ink)]/45">
                <span className="flex-1 border-t border-[var(--dahlias-sage)]/30" />or<span className="flex-1 border-t border-[var(--dahlias-sage)]/30" />
              </div>
              <button
                type="button"
                data-testid="login-google"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + "/account";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="mt-3 w-full h-11 inline-flex items-center justify-center gap-3 rounded border border-[var(--dahlias-sage)]/40 bg-white text-sm font-medium text-[var(--dahlias-ink)] hover:bg-[var(--dahlias-cream)] transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.5-5.8c-2.1 1.4-4.8 2.3-8.1 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                Continue with Google
              </button>
              <div className="mt-6 text-center text-sm text-[var(--dahlias-ink)]/70">
                <button
                  type="button"
                  onClick={() => nav("/account/signup", { state: loc.state })}
                  data-testid="login-create-account"
                  className="text-[var(--berry)] underline underline-offset-4 font-medium"
                >
                  Create a Heritage HQ account →
                </button>
              </div>
              <p className="mt-4 text-center text-xs text-[var(--dahlias-ink)]/55">
                New to HQ?{" "}
                <Link to="/account/signup" state={loc.state} className="underline" data-testid="login-create-account-link">Create your account</Link>
              </p>
            </>
          ) : reqDone ? (
            <div className="text-center py-6">
              <div className="mx-auto w-14 h-14 rounded-full bg-[var(--berry)]/15 text-[var(--berry)] flex items-center justify-center text-2xl">
                ✦
              </div>
              <h3 className="mt-4 font-display text-2xl text-[var(--dahlias-forest)]">Request received.</h3>
              <p className="mt-2 text-sm text-[var(--dahlias-ink)]/70">
                We&apos;ll review it and email you a signup link if your order is found in our system.
              </p>
              <button
                type="button"
                onClick={() => { setShowReq(false); setReqDone(false); }}
                className="mt-6 text-sm text-[var(--berry)] underline"
              >
                ← Back to sign in
              </button>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl text-[var(--dahlias-forest)]">
                Request access to Heritage Headquarters
              </h2>
              <p className="mt-1.5 text-sm text-[var(--dahlias-ink)]/70">
                Tell us who you are and we&apos;ll send a signup link if your order is in the system.
              </p>
              <form onSubmit={submitReq} className="mt-6 space-y-4" data-testid="hq-request-form">
                <AuthField label="Your name">
                  <input
                    required
                    data-testid="hq-req-name"
                    value={reqForm.name}
                    onChange={(e) => setReqForm({ ...reqForm, name: e.target.value })}
                    className="auth-input"
                  />
                </AuthField>
                <AuthField label="Email (the one you ordered with)">
                  <input
                    required
                    type="email"
                    data-testid="hq-req-email"
                    value={reqForm.email}
                    onChange={(e) => setReqForm({ ...reqForm, email: e.target.value })}
                    className="auth-input"
                  />
                </AuthField>
                <AuthField label="Phone (optional)">
                  <input
                    data-testid="hq-req-phone"
                    value={reqForm.phone}
                    onChange={(e) => setReqForm({ ...reqForm, phone: e.target.value })}
                    className="auth-input"
                  />
                </AuthField>
                <AuthField label="Note (optional)">
                  <textarea
                    data-testid="hq-req-note"
                    rows={3}
                    value={reqForm.note}
                    onChange={(e) => setReqForm({ ...reqForm, note: e.target.value })}
                    className="auth-input"
                    style={{ height: "auto", padding: "12px 16px", borderRadius: "12px" }}
                  />
                </AuthField>
                <button
                  type="submit"
                  disabled={reqBusy}
                  data-testid="hq-req-submit"
                  className="auth-submit"
                >
                  {reqBusy ? "Sending…" : "Send my request"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReq(false)}
                  className="w-full text-sm text-[var(--dahlias-ink)]/70 hover:text-[var(--berry)]"
                >
                  ← Back to sign in
                </button>
              </form>
            </>
          )}

          <style>{`
            .auth-input { width: 100%; height: 44px; padding: 0 16px; border-radius: 999px; background:white; border: 1px solid color-mix(in srgb, var(--dahlias-sage) 30%, transparent); font-size: 14px; }
            .auth-input:focus { outline: none; border-color: var(--dahlias-sage-deep); }
            .auth-submit { width: 100%; height: 48px; background: var(--berry); color: white; border-radius: 999px; font-weight: 500; letter-spacing: 0.05em; cursor: pointer; }
            .auth-submit:hover { background: var(--berry-deep); }
            .auth-submit:disabled { opacity: 0.5; cursor: not-allowed; }
          `}</style>
        </section>
      </div>
    </div>
  );
}

function AuthField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-label tracking-[0.25em] uppercase text-[var(--dahlias-sage-deep)] mb-1">{label}</span>
      {children}
    </label>
  );
}

function AuthPage({ title, subtitle, children, testid }) {
  return (
    <div data-testid={testid} className="min-h-[100svh] bg-[var(--dahlias-cream)] flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-7 md:p-9">
        <Link to="/" className="block text-center">
          <img src="/photos/brand/heritage-seal.png" alt="Heritage Dahlias" className="w-24 h-24 mx-auto" />
        </Link>
        <h1 className="mt-5 font-display text-2xl text-[var(--dahlias-forest)] text-center">{title}</h1>
        <p className="mt-2 text-sm text-[var(--dahlias-ink)]/70 text-center">{subtitle}</p>
        <div className="mt-6">{children}</div>
        <style>{`
          .auth-input { width: 100%; height: 44px; padding: 0 16px; border-radius: 999px; background:white; border: 1px solid color-mix(in srgb, var(--dahlias-sage) 30%, transparent); font-size: 14px; }
          .auth-input:focus { outline: none; border-color: var(--dahlias-sage-deep); }
          .auth-submit { width: 100%; height: 48px; background: var(--berry); color: white; border-radius: 999px; font-weight: 500; letter-spacing: 0.05em; cursor: pointer; }
          .auth-submit:hover { background: var(--berry-deep); }
          .auth-submit:disabled { opacity: 0.5; cursor: not-allowed; }
        `}</style>
      </div>
    </div>
  );
}

/* =================== Main portal =================== */

export default function CustomerPortal() {
  const nav = useNavigate();
  const [tab, setTabState] = useState(() => {
    const fromQuery = new URLSearchParams(window.location.search).get("tab");
    if (fromQuery) return fromQuery;
    if (window.location.pathname.endsWith("/messages")) return "messages";
    return "dashboard";
  });
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep the tab in the URL so refresh / share keeps your place.
  const setTab = (v) => {
    setTabState(v);
    window.history.replaceState(null, "", v === "dashboard" ? "/account" : `/account?tab=${v}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      nav("/account/login");
      return;
    }
    (async () => {
      try {
        const r = await customerApi.get("/customer/me");
        setMe(r.data);
      } catch (e) {
        clearSession();
        nav("/account/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const logout = () => {
    clearSession();
    nav("/account/login");
  };

  if (loading) {
    return <div className="min-h-[100svh] grid place-items-center text-[var(--dahlias-ink)]/60">Loading…</div>;
  }
  if (!me) return null;

  return (
    <PortalShell tab={tab} setTab={setTab} name={me.name} onLogout={logout} vipEnabled={!!me.vip_box_enabled} mysteryEnabled={!!me.vip_mystery_enabled}>
      {tab === "dashboard" && <DashboardTab me={me} setTab={setTab} />}
      {tab === "vip-box" && me.vip_box_enabled && <VipBoxBuilder embedded />}
      {tab === "mystery-box" && me.vip_mystery_enabled && <VipMysteryBox embedded />}
      {tab === "orders" && <OrdersTab />}
      {tab === "messages" && <MessagesTab name={me.name} />}
      {tab === "grower" && <MyGrowerTab />}
      {tab === "bulletin" && <BulletinTab />}
      {tab === "bloom-box" && <BloomBoxTab />}
      {tab === "account" && <AccountTab me={me} onSaved={setMe} />}
    </PortalShell>
  );
}


/* =================== My Grower tab, DMs with assigned grower =================== */

function MyGrowerTab() {
  const [grower, setGrower] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [g, m] = await Promise.all([
          customerApi.get("/customer/my-grower"),
          customerApi.get("/customer/grower-messages"),
        ]);
        if (mounted) {
          setGrower(g.data?.grower || null);
          setMessages(m.data || []);
        }
      } catch {
        if (mounted) toast.error("Couldn't load your grower.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await customerApi.post("/customer/grower-messages", { body: body.trim() });
      setBody("");
      const m = await customerApi.get("/customer/grower-messages");
      setMessages(m.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-[var(--dahlias-ink)]/55">Loading…</div>;

  if (!grower) {
    return (
      <div data-testid="my-grower-empty" className="bg-[var(--dahlias-paper)] border-2 border-dashed border-[var(--dahlias-sage)]/30 p-10 text-center wobble-a max-w-2xl">
        <Sprout className="w-10 h-10 text-[var(--dahlias-sage)] mx-auto mb-3" />
        <h3 className="font-display text-2xl text-[var(--dahlias-forest)]">No grower assigned yet</h3>
        <p className="mt-2 text-sm text-[var(--dahlias-ink)]/65 max-w-md mx-auto">
          When you join the Bloom Club, Heritage will assign you to a local grower in your
          area. You&apos;ll be able to message them here directly.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="my-grower" className="space-y-5 max-w-3xl">
      {/* Grower profile card */}
      <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 wobble-a p-5 flex gap-4 items-start">
        {grower.profile_image_url ? (
          <img src={grower.profile_image_url} alt={grower.farm_name} className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[var(--dahlias-sage)]/15 grid place-items-center">
            <Sprout className="w-7 h-7 text-[var(--dahlias-sage-deep)]" />
          </div>
        )}
        <div className="min-w-0">
          <span className="font-label tracking-[0.35em] text-[10px] text-[var(--dahlias-clay)] uppercase">✦ Your local grower ✦</span>
          <h2 className="font-display text-2xl text-[var(--dahlias-forest)] leading-tight">{grower.farm_name || grower.name}</h2>
          {grower.region && <div className="text-xs text-[var(--dahlias-ink)]/65">{grower.region}</div>}
          {grower.bio && <p className="mt-2 text-sm text-[var(--dahlias-ink)]/80 leading-relaxed line-clamp-3">{grower.bio}</p>}
        </div>
      </div>

      {/* Thread */}
      <div
        data-testid="grower-thread"
        className="bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/20 wobble-a p-5 min-h-[260px] max-h-[55vh] overflow-y-auto space-y-3"
      >
        {messages.length === 0 ? (
          <div className="text-center text-[var(--dahlias-ink)]/55 italic text-sm py-8">
            Say hi to your grower! Ask about delivery, request something special, or just chat about flowers 🌸
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2 wobble-pill text-sm leading-relaxed ${m.sender === "customer" ? "bg-[var(--berry)] text-white" : "bg-[var(--dahlias-sage)]/15 border border-[var(--dahlias-sage)]/30"}`}>
                <div className="whitespace-pre-wrap">{m.body}</div>
                <div className={`text-[10px] mt-1 ${m.sender === "customer" ? "text-white/65" : "text-[var(--dahlias-ink)]/50"}`}>
                  {new Date(m.created_at).toLocaleString("en-NZ", { dateStyle: "short", timeStyle: "short" })}
                  {m.sender === "grower" ? ` · ${grower.farm_name || grower.name}` : ""}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Message your grower…"
          data-testid="grower-message-input"
          className="flex-1 px-4 py-3 wobble-pill bg-white border border-[var(--dahlias-sage)]/30 text-sm resize-none focus:outline-none focus:border-[var(--dahlias-sage-deep)]"
        />
        <button
          onClick={send}
          disabled={sending || !body.trim()}
          data-testid="grower-message-send"
          className="self-stretch px-5 wobble-pill bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white disabled:opacity-50 inline-flex items-center gap-2"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send
        </button>
      </div>
    </div>
  );
}

/* =================== Dashboard tab (HQ landing) =================== */

function DashboardTab({ me, setTab }) {
  const [bulletins, setBulletins] = useState([]);
  const [orderCount, setOrderCount] = useState(null);
  const [recentOrder, setRecentOrder] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await customerApi.get("/customer/bulletins");
        setBulletins((r.data || []).filter((b) => b.published).slice(0, 3));
      } catch { /* ignore */ }
      try {
        const r = await customerApi.get("/customer/orders");
        setOrderCount(r.data?.total_count || 0);
        const orders = r.data?.orders || [];
        setRecentOrder(orders.find((o) => o.stage !== "completed") || orders[0] || null);
      } catch { /* ignore */ }
    })();
  }, []);

  const firstName = (me?.name || "").split(" ")[0] || "friend";

  // ─── Account snapshot stats ────────────────────────────────────────────────
  const SNAPSHOT = [
    {
      key: "credits",
      Icon: Tag,
      label: "Heritage Credits",
      value: `NZ$${Number(me?.heritage_credits || 0).toFixed(2)}`,
      cta: "View credits",
      tone: "bg-[#6E1E3A]/12 text-[#6E1E3A] border-[#6E1E3A]/30",
      onClick: () => setTab("account"),
    },
    {
      key: "orders",
      Icon: Package,
      label: "Current Orders",
      value: orderCount === null ? "…" : String(orderCount),
      cta: "View orders",
      tone: "bg-[#2F4A2A]/12 text-[#2F4A2A] border-[#2F4A2A]/30",
      onClick: () => setTab("orders"),
    },
    {
      key: "mystery",
      Icon: Gift,
      label: "Mystery Access",
      value: me?.vip_mystery_enabled ? "Active" : "Locked",
      cta: me?.vip_mystery_enabled ? "Shop mystery boxes" : "Learn more",
      tone: "bg-[#6E1E3A]/12 text-[#6E1E3A] border-[#6E1E3A]/30",
      onClick: () => me?.vip_mystery_enabled ? setTab("mystery-box") : null,
    },
    {
      key: "vip",
      Icon: Crown,
      label: "VIP Status",
      value: (VIP_BOX_FEATURE_ENABLED && me?.vip_box_enabled) ? "Active" : "Locked",
      cta: (VIP_BOX_FEATURE_ENABLED && me?.vip_box_enabled) ? "Build a VIP box" : "Learn more",
      tone: "bg-[#3E2346]/12 text-[#3E2346] border-[#3E2346]/30",
      onClick: () => (VIP_BOX_FEATURE_ENABLED && me?.vip_box_enabled) ? setTab("vip-box") : null,
    },
    {
      key: "shipment",
      Icon: Truck,
      label: "Next Shipment",
      value: recentOrder?.stage && recentOrder.stage !== "completed" ? "Pending" : "None",
      cta: recentOrder ? "View details" : "Browse catalogue",
      tone: "bg-[#2F4A2A]/12 text-[#2F4A2A] border-[#2F4A2A]/30",
      onClick: () => recentOrder ? setTab("orders") : null,
    },
  ];

  // ─── Quick action banners (4 wide stained-glass banners + My Account full-width) ─
  const QUICK_ACTIONS = [
    { v: "vip-box",     label: "VIP Build A Box", visible: !!(VIP_BOX_FEATURE_ENABLED && me?.vip_box_enabled), image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/69zk0jpq_Vipboxtab-hq.png" },
    { v: "mystery-box", label: "Mystery Box",     visible: !!me?.vip_mystery_enabled, image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/tj4bh0lm_Mysteryboxtab-%20hq.png" },
    { v: "orders",      label: "My Orders",       visible: true, image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/8v0ex0ub_Myorderstab-hq.png" },
    { v: "messages",    label: "Messages",        visible: true, image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/r3dq143p_Messagestab-hq.png" },
    { v: "account",     label: "My Account",      visible: true, image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/4s1kiat6_Myaccounttab-hq.png", wide: true },
  ].filter((q) => q.visible);

  // ─── Explore Heritage HQ (3-column icon nav) ─────────────────────────────
  const EXPLORE = [
    {
      heading: "Shop & Orders",
      Glyph: ShoppingBag,
      items: [
        ...((VIP_BOX_FEATURE_ENABLED && me?.vip_box_enabled) ? [{ Icon: Crown, label: "VIP Build A Box", note: "Create your exclusive box", onClick: () => setTab("vip-box") }] : []),
        ...(me?.vip_mystery_enabled ? [{ Icon: Gift, label: "Mystery Box", note: "Hand-picked surprises", onClick: () => setTab("mystery-box") }] : []),
        { Icon: Package, label: "My Orders", note: "View your order history", onClick: () => setTab("orders") },
        { Icon: Truck, label: "Order Tracking", note: "Track current shipments", onClick: () => setTab("orders") },
      ],
    },
    {
      heading: "Community",
      Glyph: Users,
      items: [
        { Icon: Megaphone, label: "Bulletin Board", note: "News & announcements", onClick: () => setTab("bulletin") },
        { Icon: Users, label: "Grower Network", note: "Connect & share", external: "/bloom-club/grower" },
        { Icon: Flower2, label: "Bloom Club", note: "Subscribe or learn more", onClick: () => setTab("bloom-box") },
      ],
    },
    {
      heading: "Account",
      Glyph: User,
      items: [
        { Icon: MessageCircle, label: "Messages", note: "Your Heritage inbox", onClick: () => setTab("messages") },
        { Icon: Tag, label: "Credits & Vouchers", note: "View balances & rewards", onClick: () => setTab("account") },
        { Icon: User, label: "My Account", note: "Profile & preferences", onClick: () => setTab("account") },
      ],
    },
  ];

  // ─── Discover More (4 square stained-glass tiles with baked-in text) ──────
  const DISCOVER = [
    { kicker: "Catalogue",     to: "/dahlias/catalog",       image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/r8159nwf_Catalogbottom-hq.png" },
    { kicker: "Mystery Boxes", to: "/dahlias/mystery-boxes", image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/gnckbm45_Mysterybottom-hq.png" },
    { kicker: "Bloom Club",    to: "/bloom-club",            image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/rhafbo26_Bloomclubbottom-hq.png" },
    { kicker: "Breeders Club", to: "/breeders-garden",       image: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/6gu2e636_Breedersbottom-hq.png" },
  ];

  return (
    <div className="space-y-8" data-testid="customer-dashboard">

      {/* ═══ HERO BANNER ═══════════════════════════════════════════════════ */}
      <section
        data-testid="hq-hero"
        className="relative overflow-hidden border-2 border-[#3E2346]/30 rounded-sm"
        style={{ backgroundColor: "#2A1B30" }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(42,27,48,0.92) 0%, rgba(42,27,48,0.55) 45%, rgba(42,27,48,0.20) 75%, rgba(42,27,48,0.35) 100%), url(${HERITAGE_ASSETS.heroScene})`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
          }}
        />
        <div className="relative px-6 md:px-10 py-10 md:py-14 max-w-3xl">
          <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#FBE6D8]/80">
            ✦ Heritage HQ ✦
          </span>
          <h1 className="mt-3 font-display text-4xl md:text-6xl text-[#FBE6D8] leading-[0.95]">
            Welcome back,<br />
            <span className="font-display-italic text-[#E9B7C9]">{firstName}.</span>
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#FBE6D8]/80 italic font-display">
            We&apos;re so glad you&apos;re here.
          </p>
        </div>
      </section>

      {/* ═══ ACCOUNT SNAPSHOT ═════════════════════════════════════════════ */}
      <section data-testid="hq-snapshot" className="bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/15 rounded-sm p-5 md:p-6">
        <div className="text-center mb-5">
          <span className="font-label tracking-[0.45em] text-[10px] uppercase text-[#6E1E3A]">
            ✦ Your Account Snapshot ✦
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {SNAPSHOT.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={s.onClick || (() => {})}
              data-testid={`hq-snapshot-${s.key}`}
              className="text-left bg-white border border-[#6E1E3A]/15 rounded-sm p-4 hover:border-[#6E1E3A]/40 hover:-translate-y-0.5 transition-all"
            >
              <div className={`w-10 h-10 rounded-full grid place-items-center border ${s.tone}`}>
                <s.Icon className="w-4 h-4" />
              </div>
              <div className="mt-3 font-label tracking-[0.3em] text-[9px] uppercase text-[var(--dahlias-ink)]/65">
                {s.label}
              </div>
              <div className="mt-1 font-display text-xl text-[var(--dahlias-forest)] leading-tight">
                {s.value}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#6E1E3A]">
                {s.cta} <ArrowRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ═══ QUICK ACTIONS ════════════════════════════════════════════════ */}
      <section data-testid="hq-quick-actions">
        <div className="text-center mb-4">
          <span className="font-label tracking-[0.45em] text-[10px] uppercase text-[#6E1E3A]">
            ✦ Quick Actions ✦
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 max-w-3xl mx-auto">
          {QUICK_ACTIONS.map((q) => (
            <button
              key={q.v}
              type="button"
              onClick={() => setTab(q.v)}
              data-testid={`hq-quick-${q.v}`}
              className={
                "group relative overflow-hidden bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/25 hover:border-[#6E1E3A] rounded-xl shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5 " +
                (q.wide ? "md:col-span-2 " : "")
              }
            >
              <img
                src={q.image}
                alt={q.label}
                loading="lazy"
                className="block w-full h-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
              />
            </button>
          ))}
        </div>
      </section>

      {/* ═══ EXPLORE HERITAGE HQ ═══════════════════════════════════════════ */}
      <section data-testid="hq-explore">
        <div className="text-center mb-5">
          <span className="font-label tracking-[0.45em] text-[10px] uppercase text-[#6E1E3A]">
            ✦ Explore Heritage HQ ✦
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {EXPLORE.map((col) => (
            <div key={col.heading} className="bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/15 rounded-sm p-5">
              <div className="flex items-center justify-center gap-2 pb-3 mb-3 border-b border-[#6E1E3A]/10">
                <col.Glyph className="w-3.5 h-3.5 text-[#6E1E3A]" />
                <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#6E1E3A]">
                  {col.heading}
                </span>
              </div>
              <div className="divide-y divide-[#6E1E3A]/8">
                {col.items.map((it) => {
                  const inner = (
                    <>
                      <span className="w-9 h-9 rounded-full grid place-items-center bg-[#6E1E3A]/10 text-[#6E1E3A] shrink-0">
                        <it.Icon className="w-4 h-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-display text-[15px] text-[var(--dahlias-forest)] leading-tight">{it.label}</span>
                        <span className="block text-[11px] text-[var(--dahlias-ink)]/65 mt-0.5">{it.note}</span>
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-[#6E1E3A]/60 shrink-0" />
                    </>
                  );
                  return it.external ? (
                    <Link
                      key={it.label}
                      to={it.external}
                      data-testid={`hq-explore-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="py-2.5 flex items-center gap-3 hover:bg-[#6E1E3A]/4 rounded transition"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <button
                      key={it.label}
                      type="button"
                      onClick={it.onClick}
                      data-testid={`hq-explore-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="w-full text-left py-2.5 flex items-center gap-3 hover:bg-[#6E1E3A]/4 rounded transition"
                    >
                      {inner}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ CURRENT ORDER + HERITAGE NEWS ════════════════════════════════ */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="hq-current-order" className="bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/15 rounded-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#6E1E3A]">✦ Your Current Order ✦</span>
            <button type="button" onClick={() => setTab("orders")} className="text-[11px] tracking-[0.25em] uppercase text-[#6E1E3A] inline-flex items-center gap-1 hover:underline">
              View all orders <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {recentOrder ? (
            <div>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h3 className="font-display text-xl text-[var(--dahlias-forest)]">
                  Order #{(recentOrder.short_id || recentOrder.id || "").slice(0, 8)}
                </h3>
                <span className="px-2 py-0.5 bg-[#E9C68F]/40 border border-[#A07B2C]/30 text-[10px] tracking-[0.25em] uppercase text-[#6B4A18] rounded">
                  {String(recentOrder.stage || "pending").replace(/_/g, " ")}
                </span>
              </div>
              <dl className="mt-4 grid grid-cols-3 gap-3 text-xs">
                <div>
                  <dt className="text-[#7A6A55] tracking-[0.2em] uppercase text-[9px]">Order Date</dt>
                  <dd className="font-display text-sm text-[var(--dahlias-forest)] mt-0.5">{recentOrder.created_at ? new Date(recentOrder.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "-"}</dd>
                </div>
                <div>
                  <dt className="text-[#7A6A55] tracking-[0.2em] uppercase text-[9px]">Items</dt>
                  <dd className="font-display text-sm text-[var(--dahlias-forest)] mt-0.5">{(recentOrder.items || recentOrder.line_items || []).length || "-"} varieties</dd>
                </div>
                <div>
                  <dt className="text-[#7A6A55] tracking-[0.2em] uppercase text-[9px]">Tracking</dt>
                  <dd className="font-display text-sm text-[var(--dahlias-forest)] mt-0.5">{recentOrder.tracking_number || "Pending"}</dd>
                </div>
              </dl>
              <button type="button" onClick={() => setTab("orders")} className="mt-4 inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-[#6E1E3A] hover:underline">
                View order details <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="text-sm text-[var(--dahlias-ink)]/65 italic font-display py-4">
              No active order yet. {orderCount === 0 ? "Place your first order when the catalogue opens." : "Everything you have placed is in your Orders tab."}
            </div>
          )}
        </div>

        <div data-testid="hq-news" className="bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/15 rounded-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#6E1E3A]">✦ Heritage News ✦</span>
            <button type="button" onClick={() => setTab("bulletin")} className="text-[11px] tracking-[0.25em] uppercase text-[#6E1E3A] inline-flex items-center gap-1 hover:underline">
              View all news <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {bulletins.length === 0 ? (
            <div className="text-sm text-[var(--dahlias-ink)]/65 italic font-display py-4">
              No news yet, check back soon for Heritage announcements and seasonal updates.
            </div>
          ) : (
            <ul className="divide-y divide-[#6E1E3A]/8">
              {bulletins.map((b) => (
                <li key={b.id} className="py-2.5">
                  <button type="button" onClick={() => setTab("bulletin")} className="w-full text-left flex items-start gap-3 group">
                    <span className="w-10 h-10 rounded-full grid place-items-center bg-[#6E1E3A]/10 text-[#6E1E3A] shrink-0">
                      <Megaphone className="w-4 h-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[9px] tracking-[0.3em] uppercase text-[#6E1E3A]/85">{b.category || "Bulletin"}</span>
                      <span className="block font-display text-[15px] text-[var(--dahlias-forest)] group-hover:text-[#6E1E3A] leading-tight">{b.title}</span>
                      {b.summary && <span className="block text-[11px] text-[var(--dahlias-ink)]/65 mt-0.5 line-clamp-1">{b.summary}</span>}
                    </span>
                    {b.created_at && (
                      <span className="text-[10px] text-[#7A6A55] shrink-0 mt-1">
                        {new Date(b.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ═══ PORTAL SWITCHER , one card per role the customer has ══════════ */}
      {(me?.is_grower || me?.is_subscriber || me?.is_breeder) && (
        <section data-testid="hq-portal-switcher">
          <div className="text-center mb-4">
            <span className="font-label tracking-[0.45em] text-[10px] uppercase text-[#6E1E3A]">
              ✦ Your Portals ✦
            </span>
          </div>
          <div className={`grid grid-cols-1 gap-3 ${[me?.is_grower, me?.is_subscriber, me?.is_breeder].filter(Boolean).length > 1 ? "sm:grid-cols-2 lg:grid-cols-3" : ""}`}>
            {me?.is_grower && (
              <Link
                to="/account/grower-portal"
                data-testid="hq-open-grower-portal"
                className="group relative block overflow-hidden rounded-xl border-2 border-[#3E2346]/40 shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #3E2346 0%, #7B4F82 100%)" }}
              >
                <div className="px-5 py-5 flex items-center gap-3 text-[#FBE6D8]">
                  <Sprout className="w-10 h-10 shrink-0 text-[#DCC8E6]" strokeWidth={1.25} />
                  <div className="flex-1 min-w-0">
                    <div className="font-label tracking-[0.35em] text-[10px] uppercase opacity-85">Bloom Club</div>
                    <h3 className="mt-1 font-display text-xl sm:text-2xl leading-tight">
                      <span className="font-display-italic text-[#DCC8E6]">Grower Portal</span>
                    </h3>
                    <p className="mt-1 text-xs opacity-80 italic font-display">
                      Subscribers, allocations &amp; harvest forecast.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            )}
            {me?.is_subscriber && (
              <Link
                to="/account/subscriber-portal"
                data-testid="hq-open-subscriber-portal"
                className="group relative block overflow-hidden rounded-xl border-2 border-[#3E2346]/40 shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #7B4F82 0%, #B68FBE 100%)" }}
              >
                <div className="px-5 py-5 flex items-center gap-3 text-[#FBE6D8]">
                  <Sprout className="w-10 h-10 shrink-0 text-[#FFE9D6]" strokeWidth={1.25} />
                  <div className="flex-1 min-w-0">
                    <div className="font-label tracking-[0.35em] text-[10px] uppercase opacity-85">Bloom Club</div>
                    <h3 className="mt-1 font-display text-xl sm:text-2xl leading-tight">
                      <span className="font-display-italic text-[#FFE9D6]">Subscriber Portal</span>
                    </h3>
                    <p className="mt-1 text-xs opacity-80 italic font-display">
                      Your allocations, messages and grower updates.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            )}
            {me?.is_breeder && (
              <Link
                to="/account/breeders-club"
                data-testid="hq-open-breeders-club"
                className="group relative block overflow-hidden rounded-xl border-2 border-[#3E2346]/40 shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #C49247 0%, #E8B863 100%)" }}
              >
                <div className="px-5 py-5 flex items-center gap-3 text-[#3E2346]">
                  <Sprout className="w-10 h-10 shrink-0 text-[#3E2346]" strokeWidth={1.25} />
                  <div className="flex-1 min-w-0">
                    <div className="font-label tracking-[0.35em] text-[10px] uppercase opacity-85">Breeders Club</div>
                    <h3 className="mt-1 font-display text-xl sm:text-2xl leading-tight">
                      <span className="font-display-italic">Breeders Portal</span>
                    </h3>
                    <p className="mt-1 text-xs opacity-80 italic font-display">
                      Cultivar registry, royalty tracking &amp; archive.
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            )}
          </div>
        </section>
      )}

      {/* ═══ DISCOVER MORE ════════════════════════════════════════════════ */}
      <section data-testid="hq-discover">
        <div className="text-center mb-5">
          <span className="font-label tracking-[0.45em] text-[10px] uppercase text-[#6E1E3A]">
            ✦ Discover More ✦
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {DISCOVER.map((d) => (
            <Link
              key={d.kicker}
              to={d.to}
              data-testid={`hq-discover-${d.kicker.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative bg-[var(--dahlias-paper)] border-2 border-[#6E1E3A]/25 hover:border-[#6E1E3A] rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5 aspect-square"
            >
              <div
                className="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.02]"
                style={{
                  backgroundImage: `url(${d.image})`,
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundColor: "var(--dahlias-paper)",
                }}
              />
            </Link>
          ))}
        </div>
      </section>

      {/* ═══ FOOTER QUICK BAR ═════════════════════════════════════════════ */}
      <section data-testid="hq-footer-bar" className="bg-[#2A1B30] text-[#FBE6D8] rounded-sm border-2 border-[#3E2346]/40">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#FBE6D8]/15">
          {[
            { Icon: Tag, label: "Credits & Vouchers", note: "Redeem at checkout", onClick: () => setTab("account") },
            { Icon: Truck, label: "Shipping Information", note: "Rates & delivery info", to: "/dahlias/about" },
            { Icon: Settings, label: "Account Settings", note: "Email, password, details", onClick: () => setTab("account") },
            { Icon: LogOut, label: "Sign Out", note: "See you soon!", onClick: () => { localStorage.removeItem("heritage-customer-token"); window.location.assign("/account/login"); } },
          ].map((f) => {
            const inner = (
              <>
                <f.Icon className="w-5 h-5 text-[#E9B7C9] shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block font-label tracking-[0.3em] text-[10px] uppercase text-[#E9B7C9]">{f.label}</span>
                  <span className="block text-[11px] text-[#FBE6D8]/70 mt-0.5 italic font-display">{f.note}</span>
                </span>
              </>
            );
            return f.to ? (
              <Link key={f.label} to={f.to} className="px-4 py-4 flex items-center gap-3 hover:bg-white/5 transition" data-testid={`hq-footer-${f.label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</Link>
            ) : (
              <button key={f.label} type="button" onClick={f.onClick} className="px-4 py-4 flex items-center gap-3 hover:bg-white/5 transition text-left" data-testid={`hq-footer-${f.label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/* =================== Bloom Box Interest tab, outbound to Bloom Club ============ */

function BloomBoxTab() {
  const ROWS = [
    {
      kicker: "Reserve",
      title: "Bloom Box subscription",
      note: "A seasonal box of fresh blooms delivered straight from the farm. Reserve your spot for the next season.",
      to: "/bloom-club/join",
      cta: "Reserve a Bloom Box",
      Icon: Mail,
    },
    {
      kicker: "Waitlist",
      title: "Join the Bloom Club waitlist",
      note: "Be the first to know when the Bloom Club opens new spots, with priority access to limited releases.",
      to: "/bloom-club/join",
      cta: "Join the waitlist",
      Icon: Sparkles,
    },
    {
      kicker: "Apply",
      title: "Become a Bloom Club grower",
      note: "Grow Heritage dahlias as part of our regional grower network. Apply to be considered for the next intake.",
      to: "/bloom-club/grower/apply",
      cta: "Apply to grow",
      Icon: Flower2,
    },
  ];

  return (
    <div className="space-y-6" data-testid="customer-bloom-box">
      <header>
        <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#6E1E3A]">✦ Bloom Club ✦</span>
        <h1 className="mt-2 font-display text-3xl md:text-4xl text-[var(--dahlias-forest)] leading-tight">
          Behind the <span className="font-display-italic text-[#6E1E3A]">garden gate.</span>
        </h1>
        <p className="mt-3 text-sm md:text-base text-[var(--dahlias-ink)]/80 max-w-2xl">
          Bloom Club is a separate Heritage community for bouquet subscribers and our grower network. Reserve a box, join the waitlist, or apply to grow, all the action lives on the Bloom Club site.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        {ROWS.map((r) => (
          <Link
            key={r.title}
            to={r.to}
            data-testid={`bloom-box-${r.kicker.toLowerCase()}`}
            className="group bg-white border-2 border-[#6E1E3A]/20 hover:border-[#6E1E3A]/60 p-6 wobble-a shadow-md hover:shadow-xl transition-all hover:-translate-y-1 flex flex-col"
          >
            <span className="w-12 h-12 grid place-items-center wobble-blob bg-[#6E1E3A]/15 text-[#6E1E3A]">
              <r.Icon className="w-6 h-6" />
            </span>
            <span className="mt-4 font-label tracking-[0.4em] text-[10px] uppercase text-[#6E1E3A]/85">{r.kicker}</span>
            <h3 className="mt-1 font-display text-2xl text-[var(--dahlias-forest)] leading-tight group-hover:text-[#6E1E3A] transition-colors">
              {r.title}
            </h3>
            <p className="mt-2 text-sm text-[var(--dahlias-ink)]/75 leading-relaxed flex-1">{r.note}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-[#6E1E3A] text-xs font-label tracking-[0.25em] uppercase">
              {r.cta} <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

